import React, { useMemo } from 'react';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { BasicUserInfo, UserInfo } from '@shared/models/user.models';
import styled from "styled-components";
import { Center, Row } from '@shared/ui/atoms/StructuralLayout';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { UserSelectType } from '@shared/dialogs/user-search-dialog/UserSearchDialog';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { useDispatch, useSelector } from 'react-redux';
import {
  addLineReport,
  removeLineReport,
  selectEdittingUser,
  selectLineReports
} from '@features/settings/store/editUser.duck';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { selectCurrentUser } from "@shared/store/auth.duck";
import { canManageLineReports } from "@shared/utils/AccessControl";
import { naturalSort } from "@shared/utils/ArrayUtils";
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { useTranslation } from "react-i18next";

interface Props {
  user: UserInfo;
}

export default function UsersLineReports(props: Props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const currentEdittingUser = useSelector(selectEdittingUser);
  const lineReports = useSelector(selectLineReports);
  const canUserManageLineReports = useMemo(() => canManageLineReports(currentUser), [currentUser]);
  const {t} = useTranslation();

  const orderedLineReports = useMemo(() => {
    try {
      return naturalSort([...lineReports], 'fullName')
    } catch (e: any) {
      return lineReports;
    }
  }, [lineReports]);

  const onUserSelect = (user: BasicUserInfo) => {
    if (currentEdittingUser) {
      const isLineReport = !!orderedLineReports.find(lr => lr.userId === user.userId);

      if (isLineReport) {
        dispatch(removeLineReport({subordinateUserId: user.userId, reportingToUserId: currentEdittingUser.userId}));
      } else {
        dispatch(addLineReport({subordinateUserId: user.userId, reportingToUserId: currentEdittingUser.userId}));
      }
    }
  }

  const onDeleteClicked = (lineReport: BasicUserInfo) => {
    if (currentEdittingUser) {
      dispatch(removeLineReport({subordinateUserId: lineReport.userId, reportingToUserId: currentEdittingUser.userId}));
    }
  }

  if (!currentUser?.companyEntity.lineManagementEnabled) {
    return null;
  }

  return (
    <Wrapper className="" data-test="location-section">
      <BodyLarge colour={Colours.blue} style={{marginBottom: 6}}>{t('settings.line-reports')}</BodyLarge>
      <BodyRegular style={{marginBottom: 16}}>{t('settings.users-reporting-to-this-user')}</BodyRegular>
      {orderedLineReports.length  > 0 ?
        <>
          {orderedLineReports.map((lineReport: BasicUserInfo, key: any) => (
            <LineReportRow key={key} className={"lineReports__lineReport"}>
              <UserAvatarAndName firstName={lineReport.firstName} lastName={lineReport.lastName} />
              <IconButton icon={IconTypes.Bin} onClick={() => onDeleteClicked(lineReport)} disabled={!canUserManageLineReports} />
            </LineReportRow>
          ))}
        </>
      :
      (
        <Row>
          <Center style={{backgroundColor: Colours.veryLightGrey, alignItems: 'center', borderRadius: 16, minHeight: 80, marginBottom: 24}}>
            <BodyRegular colour={Colours.darkGrey}>{t('settings.no-line-reports')}</BodyRegular>
          </Center>
        </Row>
      )
      }
      <Center style={{marginBottom: 24}}>
        <OutlineButton click={() => dispatch(openDialogWithPayload({
                         activeDialog: DialogIdentifiers.UserSearchDialog,
                         payload: {
                           onClose: () => {},
                           title: "dialog-headers.select-line-reports",
                           description: "dialog.selected-number-of-users-to-report-to-a-user",
                           type: UserSelectType.multiSelect,
                           onUserSelect: onUserSelect,
                           selectedUsers: orderedLineReports,
                         },
                       }))}
                       disabled={!canUserManageLineReports}
                       dataTest={'add-new-line-report-button'}
                       text="button.add-new-line-report" />
      </Center>
    </Wrapper>
  )
}


const Wrapper = styled.div`
  .teamDropDownList {
    width: 100%;
  }
  border-bottom: 1px solid ${Colours.mildGrey};
  border-top: 1px solid ${Colours.mildGrey};
  margin-bottom: 24px;
  padding-bottom: 12px;
  padding-top: 16px;
  margin-top: 16px;
`

const LineReportRow = styled.div`
  margin: 8px 0;
  padding: 8px 4px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:hover {
    background-color: ${Colours.blue10};
  }
`
